export type BillingMode = "platform" | "byok";

export type ProviderMode = "off" | "platform" | "byok" | "auto";

export type ProviderSetting = {
  provider: string;
  name: string;
  enabled: boolean;
  byokAvailable: boolean;
  platformAllowed: boolean;
  mode: ProviderMode;
  effectiveState: "active" | "excluded";
  effectiveReason: string | null;
  credential: {hint: string; status: "active" | "invalid"; createdAt: string} | null;
};

export type ProviderCredentialResult = {
  hint: string;
  status: string;
  warning: string | null;
};

export type DashboardData = {
  viewer: {
    userId: string;
    accountId: string;
    organizationId: string | null;
    role: string | null;
    permissions: string[];
  };
  wallet: {
    configured: boolean;
    availableMicros: number;
    reservedMicros: number;
    pendingMicros: number;
    currency: string;
  };
  models: Array<{
    id: string;
    clusterName: string;
    region: string;
    status: "ready" | "offline" | "draining";
    inputPriceMicrosPerMillion: number;
    outputPriceMicrosPerMillion: number;
    lastSeenAt: string | null;
    providers: string[];
  }>;
  byokEnabled: boolean;
  apiKeys: Array<{
    id: string;
    name: string;
    prefix: string;
    createdAt: string;
    revokedAt: string | null;
  }>;
  usage: Array<{
    requestId: string;
    model: string;
    inputTokens: number;
    outputTokens: number;
    chargeMicros: number;
    billingMode: BillingMode;
    completedAt: string;
  }>;
  providerSettings: ProviderSetting[];
  usageBuckets: Array<{
    hour: string;
    model: string;
    billingMode: BillingMode;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    spendMicros: number;
  }>;
  viewerUsage: Array<{
    hour: string;
    model: string;
    billingMode: BillingMode;
    requests: number;
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    chargeMicros: number;
  }>;
  modelPerformance: Array<{
    model: string;
    errorRate: number | null;
    averageTtfbMs: number | null;
    tokensPerSecond: number | null;
    volumeTokens: number;
  }>;
};

export type PublicUsageBucket = {
  hour: string;
  requests: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
};

export type CreatedApiKey = DashboardData["apiKeys"][number] & {secret: string};

const dashboardQuery = /* GraphQL */ `
  query Dashboard {
    viewer { userId accountId organizationId role permissions }
    wallet { configured availableMicros reservedMicros pendingMicros currency }
    models { id clusterName region status inputPriceMicrosPerMillion outputPriceMicrosPerMillion lastSeenAt providers }
    byokEnabled
    apiKeys { id name prefix createdAt revokedAt }
    usage(limit: 100) { requestId model inputTokens outputTokens chargeMicros billingMode completedAt }
  }
`;

const providersQuery = /* GraphQL */ `
  query Providers {
    providerSettings {
      provider name enabled byokAvailable platformAllowed mode effectiveState effectiveReason
      credential { hint status createdAt }
    }
  }
`;

const usageBucketsQuery = /* GraphQL */ `
  query UsageBuckets {
    usageBuckets(hours: 672) { hour model billingMode requests inputTokens outputTokens spendMicros }
  }
`;

const dashboardAnalyticsQuery = /* GraphQL */ `
  query DashboardAnalytics {
    viewerUsage(hours: 672) {
      hour model billingMode requests inputTokens outputTokens totalTokens chargeMicros
    }
    modelPerformance: viewerModelPerformance(hours: 168) {
      model errorRate averageTtfbMs tokensPerSecond volumeTokens
    }
  }
`;

async function graphql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  const response = await fetch("/api/graphql", {
    method: "POST",
    credentials: "same-origin",
    headers: {"content-type": "application/json"},
    body: JSON.stringify({query, variables}),
  });
  const payload = await response.json().catch(() => null) as {data?: T; errors?: Array<{message: string}>} | null;
  if (!response.ok || !payload?.data || payload.errors?.length) {
    throw new Error(payload?.errors?.[0]?.message ?? `The request failed (${response.status}). Try again.`);
  }
  return payload.data;
}

export async function fetchDashboard(): Promise<DashboardData> {
  const [dashboard, usageBuckets, analytics, providers] = await Promise.all([
    graphql<Omit<DashboardData, "usageBuckets" | "viewerUsage" | "modelPerformance" | "providerSettings">>(dashboardQuery),
    graphql<Pick<DashboardData, "usageBuckets">>(usageBucketsQuery)
      .catch(() => ({usageBuckets: []})),
    graphql<Pick<DashboardData, "viewerUsage" | "modelPerformance">>(dashboardAnalyticsQuery)
      .catch(() => ({viewerUsage: [], modelPerformance: []})),
    // Providers are optional plumbing: an account without the control-plane
    // tables still renders the rest of the dashboard.
    graphql<Pick<DashboardData, "providerSettings">>(providersQuery)
      .catch(() => ({providerSettings: []})),
  ]);
  return {...dashboard, ...usageBuckets, ...analytics, ...providers};
}

export async function fetchPublicUsage(): Promise<PublicUsageBucket[]> {
  const response = await fetch("/api/public-usage", {headers: {accept: "application/json"}});
  const payload = await response.json().catch(() => null) as {usage?: PublicUsageBucket[]; error?: string} | null;
  if (!response.ok || !Array.isArray(payload?.usage)) {
    throw new Error(payload?.error ?? `Usage API returned ${response.status}`);
  }
  return payload.usage;
}

export async function createApiKey(name: string): Promise<CreatedApiKey> {
  const data = await graphql<{createApiKey: CreatedApiKey}>(`
    mutation CreateApiKey($name: String!) {
      createApiKey(name: $name) { id name prefix secret createdAt revokedAt }
    }
  `, {name});
  return data.createApiKey;
}

export async function revokeApiKey(id: string): Promise<boolean> {
  const data = await graphql<{revokeApiKey: boolean}>(`
    mutation RevokeApiKey($id: ID!) { revokeApiKey(id: $id) }
  `, {id});
  return data.revokeApiKey;
}

export async function setProviderMode(provider: string, mode: ProviderMode): Promise<ProviderSetting> {
  const data = await graphql<{setProviderMode: ProviderSetting}>(`
    mutation SetProviderMode($provider: ID!, $mode: String!) {
      setProviderMode(provider: $provider, mode: $mode) {
        provider name enabled byokAvailable platformAllowed mode effectiveState effectiveReason
        credential { hint status createdAt }
      }
    }
  `, {provider, mode});
  return data.setProviderMode;
}

export async function setProviderCredential(
  provider: string,
  key: string,
  attested: boolean,
): Promise<ProviderCredentialResult> {
  const data = await graphql<{setProviderCredential: ProviderCredentialResult}>(`
    mutation SetProviderCredential($provider: ID!, $key: String!, $attested: Boolean!) {
      setProviderCredential(provider: $provider, key: $key, attested: $attested) { hint status warning }
    }
  `, {provider, key, attested});
  return data.setProviderCredential;
}

export async function deleteProviderCredential(provider: string): Promise<boolean> {
  const data = await graphql<{deleteProviderCredential: boolean}>(`
    mutation DeleteProviderCredential($provider: ID!) { deleteProviderCredential(provider: $provider) }
  `, {provider});
  return data.deleteProviderCredential;
}

export async function createCreditCheckout(amountDollars: number): Promise<string> {
  const data = await graphql<{createCreditCheckout: {url: string}}>(`
    mutation CreateCreditCheckout($amountDollars: Int!) {
      createCreditCheckout(amountDollars: $amountDollars) { url }
    }
  `, {amountDollars});
  return data.createCreditCheckout.url;
}
